import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

// ---- Module-scope caches (stale-while-revalidate) ---------------------------
// Filters are shared across every module page and effectively static within a
// session, so we fetch them once, share the promise, and expire after 5 min.
const FILTER_TTL_MS = 5 * 60 * 1000;
const EMPTY_FILTERS = {
  counselling_bodies: [],
  years: [],
  rounds: [],
  colleges: [],
  branches: [],
  quotas: [],
  categories: [],
  category_codes: [],
  states: [],
};
let filtersCache = null; // { at: number, data: {...} }
let filtersInflight = null;

const fetchFilters = () => {
  const fresh = filtersCache && Date.now() - filtersCache.at < FILTER_TTL_MS;
  if (fresh) return Promise.resolve(filtersCache.data);
  if (filtersInflight) return filtersInflight;
  filtersInflight = api
    .get("/filters")
    .then(({ data }) => {
      filtersCache = { at: Date.now(), data };
      return data;
    })
    .catch(() => filtersCache?.data ?? EMPTY_FILTERS)
    .finally(() => {
      filtersInflight = null;
    });
  return filtersInflight;
};

export const useFilterOptions = () => {
  const [opts, setOpts] = useState(filtersCache?.data ?? EMPTY_FILTERS);
  useEffect(() => {
    let alive = true;
    fetchFilters().then((data) => {
      if (alive) setOpts(data);
    });
    return () => {
      alive = false;
    };
  }, []);
  return opts;
};

// Per-endpoint,params cache lets a tab that was visited earlier redraw its
// data table instantly (no "Loading…" flash) while a silent refresh runs.
const moduleCache = new Map(); // key -> { rows, total, locked, visible, trial_limit? }
const cacheKey = (endpoint, cleanParams) => `${endpoint}?${JSON.stringify(cleanParams)}`;

export const useModuleData = (endpoint, params) => {
  const paramsKey = JSON.stringify(params ?? {});
  const cleanParams = useMemo(() => {
    const parsed = JSON.parse(paramsKey);
    return Object.fromEntries(
      Object.entries(parsed).filter(([, value]) => value !== "" && value != null),
    );
  }, [paramsKey]);
  const key = cacheKey(endpoint, cleanParams);

  const cached = moduleCache.get(key);
  const [data, setData] = useState(cached ?? { rows: [], total: 0, locked: false, visible: 0 });
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const previous = moduleCache.get(key);
    if (!previous) setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.get(endpoint, { params: cleanParams });
      moduleCache.set(key, res);
      setData(res);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint, cleanParams, key]);

  useEffect(() => {
    // Immediately reflect any cache hit produced by a sibling mount.
    const hit = moduleCache.get(key);
    if (hit) setData(hit);
    load();
  }, [key, load]);

  return { ...data, loading, error, reload: load };
};

export const saveSearch = async (module, params) => {
  const name = window.prompt("Name this search", `${module} view`);
  if (!name) return;
  try {
    await api.post("/saved-searches", { name, module, params });
    toast.success("Search saved");
  } catch (e) {
    toast.error(errMsg(e));
  }
};

export const addToWatchlist = async (college, branch) => {
  try {
    const { data } = await api.post("/watchlist", { college, branch });
    toast.success(data.already ? "Already in watchlist" : "Added to watchlist");
  } catch (e) {
    toast.error(errMsg(e));
  }
};
